import type { RouterOutputs } from '@package/api'

/** Seat counts are uncapped at checkout for both family and school. */
const UNCAPPED_SEATS = Number.MAX_SAFE_INTEGER

export const FAMILY_MINIMUM_SEATS = 1
export const FAMILY_MAXIMUM_SEATS = UNCAPPED_SEATS
export const SCHOOL_MINIMUM_SEATS = 1
export const SCHOOL_MAXIMUM_SEATS = UNCAPPED_SEATS

/** Minimum seats when adding on (same for family and school). */
export const ADD_ON_MINIMUM_SEATS = 1
/** Maximum seats when adding on (same for family and school). */
export const ADD_ON_MAXIMUM_SEATS = UNCAPPED_SEATS

export interface AddOnCheckoutProps {
  pricePerSeat: number
  duration: number
  totalSeat: number
}

export interface NewCheckoutProps {
  selectedPricePerSeat: number
  selectedPriceDuration: number
  theMostExpensivePricePerSeat: number
  totalSeat: number
  voucher: RouterOutputs['price']['getActiveVouchers'][number] | null
  /**
   * Money in the order that is NOT `rate x seats x duration` — a renewal's pro-rata bridge for
   * empty seats that start at payment (`price.getRenewalBridgeQuote`). Part of the total the
   * voucher discounts, and of the amount paid; not part of the term or tier savings, which compare
   * rate-card rows and have nothing to say about days on a pool already bought.
   */
  extraAmount?: number
}

/** The order before any voucher: the term at the selected rate, plus whatever rides on top. */
function amountBeforeVoucher(props: NewCheckoutProps) {
  return props.selectedPricePerSeat * props.totalSeat * props.selectedPriceDuration + (props.extraAmount ?? 0)
}

export type CalculatePriceProps =
  | {
      type: 'new'
      data: NewCheckoutProps
    }
  | {
      type: 'add-on'
      data: AddOnCheckoutProps
    }

export function calculateTotalPriceWithoutVoucher(props: CalculatePriceProps) {
  if (props.type === 'new') {
    return amountBeforeVoucher(props.data)
  }

  return props.data.pricePerSeat * props.data.totalSeat * props.data.duration
}

export function calculateSavings(props: NewCheckoutProps) {
  const isTheSamePrice = props.selectedPricePerSeat === props.theMostExpensivePricePerSeat

  if (isTheSamePrice) return 0

  return (
    (props.theMostExpensivePricePerSeat - props.selectedPricePerSeat) * props.totalSeat * props.selectedPriceDuration
  )
}

export function calculateVoucherDiscount(props: NewCheckoutProps) {
  const base = amountBeforeVoucher(props)

  if (!props.voucher) return 0

  const voucherValue = Number(props.voucher.value)

  let realVoucherValue = voucherValue

  if (props.voucher.type === 'PERCENTAGE') {
    // `!== null`, not truthiness: the server caps on the same test, and a `maximumValue` of 0 is a
    // cap of zero — a voucher that discounts nothing. Read as falsy it became "no cap at all", so
    // the form quoted a discount the invoice then refused to give.
    if (props.voucher.maximumValue !== null && props.voucher.maximumValue !== undefined) {
      realVoucherValue = Math.floor(Math.min(base * (voucherValue / 100), props.voucher.maximumValue))
    } else {
      realVoucherValue = Math.floor(base * (voucherValue / 100))
    }
  } else {
    realVoucherValue = Math.floor(voucherValue)
  }

  if (realVoucherValue > base) return base

  return realVoucherValue
}

/**
 * Everything the order saved, in one number: the volume tier and the voucher.
 *
 * TERM SAVINGS ARE DELIBERATELY EXCLUDED. `calculateSavings` compares the chosen term against the
 * dearest term in the same tier, which is a comparison between two different things to buy — a
 * twelve-month order has not "saved" against the three-month order nobody placed. Adding it to a
 * total the buyer reads as money off this order overstated every long-term purchase. It is still
 * shown on its own line ("Hemat paket"), where the comparison it makes is visible.
 *
 * `tierSavings` comes in as an argument rather than being derived here because it needs the whole
 * rate card (see `calculateTierSavings`), which `NewCheckoutProps` deliberately does not carry —
 * these props are the shape the charge is computed from, and the rate card is not part of it.
 */
export function calculateTotalDiscount(props: NewCheckoutProps, tierSavings = 0) {
  const voucherDiscount = calculateVoucherDiscount(props)

  return tierSavings + voucherDiscount
}

export function calculateAmountPaid(props: NewCheckoutProps) {
  const base = amountBeforeVoucher(props)
  const voucherDiscount = calculateVoucherDiscount(props)

  if (voucherDiscount > base) return 0

  return base - voucherDiscount
}

/** One row of the public rate card. */
interface RateCardRow {
  id: string
  price: number
  duration: number
  tierMinSeats: number
  /** Null means open-ended — the "50+ seats" tier. */
  tierMaxSeats: number | null
}

/**
 * The rate card narrowed to one seat count: one row per billing term, at the tier that seat count
 * falls in.
 *
 * Display only. `computeCheckoutCharge` resolves the tier again server-side and that result is
 * what is charged — this exists so the form can show the buyer the same number without a refetch
 * on every press of the seat stepper.
 *
 * Bounds inclusive, `tierMaxSeats` null meaning open-ended.
 */
export function pricesForSeats<T extends RateCardRow>(prices: T[], seats: number): T[] {
  return prices.filter((row) => seats >= row.tierMinSeats && (row.tierMaxSeats === null || seats <= row.tierMaxSeats))
}

/**
 * The row for the same billing term in whichever tier `seats` now falls in.
 *
 * Crossing a tier boundary with the stepper must not silently keep charging the old tier's row, and
 * must not reset the term the buyer picked. Falls back to the current row when the new tier has no
 * row for that term, which is a rate-card gap rather than something the form can fix.
 */
export function repointPriceToTier<T extends RateCardRow>(prices: T[], current: T, seats: number): T {
  return pricesForSeats(prices, seats).find((row) => row.duration === current.duration) ?? current
}

/**
 * How many more seats buy a cheaper per-seat rate, or null when none does.
 *
 * Volume pricing is invisible from inside a tier: a buyer at 8 seats sees one price and no reason
 * to think 10 costs less per seat. This is the nudge — the NEAREST tier above the current seat
 * count, for the term already chosen, and only when its rate is actually lower.
 *
 * Same term only. Suggesting the twelve-month tier to someone buying three months would be quoting
 * a discount they can only have by changing what they are buying.
 */
export function nextTierUpsell<T extends RateCardRow>(
  prices: T[],
  current: { duration: number; price: number },
  seats: number,
): { seatsNeeded: number; pricePerSeat: number } | null {
  const cheaperAhead = prices
    .filter((row) => row.duration === current.duration && row.tierMinSeats > seats && row.price < current.price)
    // Nearest boundary first: the cheapest tier may be forty seats away, which is not a nudge.
    .sort((a, b) => a.tierMinSeats - b.tierMinSeats)[0]

  if (!cheaperAhead) return null

  return { seatsNeeded: cheaperAhead.tierMinSeats - seats, pricePerSeat: cheaperAhead.price }
}

/**
 * The longer billing term that costs less per seat per month, or null when none does.
 *
 * The seat nudge's sibling, for the package dropdown: a buyer reading "Rp26.650 per kursi per
 * bulan" has no way to see that the same tier at twelve months is cheaper every month — the
 * dropdown lists terms, not rates.
 *
 * Same tier only, so it never quotes a rate that depends on buying seats as well. Longest saving
 * wins, with the shorter term breaking a tie: the point of the line is the biggest number it can
 * honestly show, and between two equal rates the smaller commitment is the easier yes.
 */
export function longerTermUpsell<T extends RateCardRow>(
  prices: T[],
  current: { id: string; duration: number; price: number },
  seats: number,
): T | null {
  return (
    pricesForSeats(prices, seats)
      .filter((row) => row.duration > current.duration && row.price < current.price)
      .sort((a, b) => a.price - b.price || a.duration - b.duration)[0] ?? null
  )
}

/**
 * What the VOLUME tier is worth: this order priced at the entry tier's rate, minus what it costs at
 * the tier the seat count actually reaches.
 *
 * Distinct from `calculateSavings`, which compares terms inside one tier and therefore answers
 * "what the longer package saved". Both are display-only — the invoice records only the voucher as
 * discount, because tier and term are the price rather than a reduction of it.
 *
 * Zero when the buyer sits in the entry tier, or when the rate card has no row for their term
 * there.
 */
export function calculateTierSavings<T extends RateCardRow>(
  prices: T[],
  current: { duration: number; price: number },
  seats: number,
): number {
  const entryTier = prices
    .filter((row) => row.duration === current.duration)
    .sort((a, b) => a.tierMinSeats - b.tierMinSeats)[0]

  if (!entryTier || entryTier.price <= current.price) return 0

  return (entryTier.price - current.price) * seats * current.duration
}
